import { useMemo } from 'react'

import { Avatar, Box, MenuItem, Typography } from '@mui/material'
import type { SimpleSelectOption } from '@workwave-tidal/core/components'

import { getColorValue } from '@/styles'

import * as OrderTexts from '../../../../../../intl'
import { form } from '../../../formState'
import { Props } from '../typings'

type VehicleColorAvatarProps = {
  color?: string
  size?: number
}

function VehicleColorAvatar({ color, size = 20 }: VehicleColorAvatarProps) {
  return (
    <Avatar
      variant="circular"
      sx={{
        width: size,
        height: size,
        bgcolor: color || 'transparent',
        border: color ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
      }}
    >
      <span />
    </Avatar>
  )
}

function useDropdownOptions(
  vehicles: uui.domain.client.rm.Vehicle[],
): SimpleSelectOption<string>[] {
  return useMemo(() => {
    const vehicleOptions: SimpleSelectOption<string>[] = vehicles.map(
      ({ id, externalId, color }) => ({
        value: id,
        label: externalId,
        icon: <VehicleColorAvatar color={getColorValue(color)} />,
      }),
    )

    return vehicleOptions
  }, [vehicles])
}

export function Vehicle(props: Props) {
  const options = useDropdownOptions(props.vehicles)

  // Custom render for options in the dropdown
  const renderVehicleOption = (option: SimpleSelectOption<string>) => (
    <MenuItem key={option.value} value={option.value}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {option.icon}
        <Typography variant="body1">{option.label}</Typography>
      </Box>
    </MenuItem>
  )

  // Custom render for selected value
  const renderVehicleValue = (option: SimpleSelectOption<string>) => {
    if (!option) return null
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {option.icon}
        <Typography variant="body1">{option.label}</Typography>
      </Box>
    )
  }

  return (
    <form.SimpleSelect
      label={OrderTexts.getFieldLabelVehicleAssignmentText()}
      name="order.forceVehicleId"
      testId="forceVehicleId"
      options={options}
      renderOptions={options => options.map(option => renderVehicleOption(option))}
      renderValue={renderVehicleValue}
      selectProps={{ MenuProps: { PaperProps: { style: { maxHeight: '300px' } } } }}
      clearable
    />
  )
}
